/**
 * Telemetry Policies — admin CRUD, event catalog, and project assignment.
 * Trailing slashes per backend routing (FastAPI 307 avoidance).
 * update() accepts an optional If-Match policy_version for optimistic
 * concurrency (D1).
 */

import type {
  EventCatalog,
  PolicyComplianceResponse,
  ProjectTelemetryPolicyResponse,
  TelemetryPolicy,
  TelemetryPolicyCreate,
  TelemetryPolicyRevision,
  TelemetryPolicyUpdate,
} from '@/types/telemetryPolicy'
import { apiClient } from './client'
import { optionalIfMatchHeaders } from './ifMatch'

export const telemetryPolicies = {
  list: async (): Promise<TelemetryPolicy[]> => {
    const response = await apiClient.get<TelemetryPolicy[]>('/api/v1/policies/')
    return response.data
  },

  get: async (id: string): Promise<TelemetryPolicy> => {
    const response = await apiClient.get<TelemetryPolicy>(`/api/v1/policies/${id}/`)
    return response.data
  },

  create: async (body: TelemetryPolicyCreate): Promise<TelemetryPolicy> => {
    const response = await apiClient.post<TelemetryPolicy>('/api/v1/policies/', body)
    return response.data
  },

  // ifMatch is the current policy_version; omit for last-write-wins semantics.
  // A mismatch returns 412 — surfaced by the 412 branch of handleMutationError.
  update: async (
    id: string,
    body: TelemetryPolicyUpdate,
    ifMatch?: number
  ): Promise<TelemetryPolicy> => {
    const response = await apiClient.put<TelemetryPolicy>(
      `/api/v1/policies/${id}/`,
      body,
      optionalIfMatchHeaders(ifMatch)
    )
    return response.data
  },

  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/policies/${id}/`)
  },

  revisions: async (
    id: string,
    opts?: { skip?: number; limit?: number }
  ): Promise<TelemetryPolicyRevision[]> => {
    const response = await apiClient.get<TelemetryPolicyRevision[]>(
      `/api/v1/policies/${id}/revisions/`,
      { params: { skip: opts?.skip ?? 0, limit: opts?.limit ?? 50 } }
    )
    return response.data
  },

  // Returns { events: EventCatalog } — the per-CLI event sets derived from the
  // normalizer registry (D11). staleTime Infinity in the hook: changes only on deploy.
  eventCatalog: async (): Promise<{ events: EventCatalog }> => {
    const response = await apiClient.get<{ events: EventCatalog }>(
      '/api/v1/policies/event-catalog/'
    )
    return response.data
  },

  assign: async (
    projectId: string,
    policyId: string
  ): Promise<ProjectTelemetryPolicyResponse> => {
    const response = await apiClient.put<ProjectTelemetryPolicyResponse>(
      `/api/v1/projects/${projectId}/telemetry-policy/`,
      { policy_id: policyId }
    )
    return response.data
  },

  unassign: async (projectId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/projects/${projectId}/telemetry-policy/`)
  },

  // Trailing slash required: FastAPI 307-redirects slash-less paths before auth,
  // so the client would follow an unauthenticated redirect and receive a 401.
  compliance: async (): Promise<PolicyComplianceResponse> => {
    const response = await apiClient.get<PolicyComplianceResponse>(
      '/api/v1/policies/compliance/'
    )
    return response.data
  },
}
